/**
 * Stuffed Cookie Selection Modal
 * 
 * Modal that appears when customer clicks "Add to Cart" on stuffed cookies.
 * Allows customers to select which cookie varieties they want.
 * 
 * Features:
 * - Single cookie: Dropdown to select one flavor
 * - Half dozen/Dozen: Quantity selectors for each flavor (must sum to total)
 * - Loads flavors dynamically from Product Options API
 * - Shows all available varieties that Tammy has configured
 */

import { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { getTotalCookiesFromName } from '../utils/cookiePieces';

const API_URL = import.meta.env.VITE_API_URL || '';

interface StuffedCookieSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selection: string) => void;
  productId: string;
  productName: string;
  productPrice: number;
  /** When opening from "Change flavours" in cart, pass current notes to pre-fill the modal */
  initialNotes?: string;
  /** When true, show "Update flavours" instead of "Add to Cart" (cart edit mode) */
  isEditMode?: boolean;
}

// Fallback flavors if API fails
const FALLBACK_FLAVORS = ['Brownie', 'Butterfinger', 'Caramilk'];

/** Parse notes from cart into single flavor or flavor quantities for pre-fill */
function parseNotesToPreFill(notes: string, isSingle: boolean): { singleFlavor: string; flavorQuantities: Record<string, number> } {
  const singleFlavor = '';
  const flavorQuantities: Record<string, number> = {};
  if (!notes?.trim()) return { singleFlavor, flavorQuantities };
  if (isSingle) {
    const m = notes.match(/^\s*Flavor:\s*(.+)$/i);
    return { singleFlavor: m ? m[1].trim() : '', flavorQuantities };
  }
  // Multi: "2 Brownie, 3 Butterfinger, 1 Caramilk"
  const parts = notes.split(',').map(p => p.trim());
  for (const part of parts) {
    const m = part.match(/^(\d+)\s+(.+)$/);
    if (m) {
      const qty = parseInt(m[1], 10);
      const name = m[2].trim();
      if (name && !Number.isNaN(qty)) flavorQuantities[name] = (flavorQuantities[name] || 0) + qty;
    }
  }
  return { singleFlavor, flavorQuantities };
}

export default function StuffedCookieSelectionModal({
  isOpen,
  onClose,
  onConfirm,
  productId: _productId, // kept for API/future use; not used in modal UI
  productName,
  productPrice,
  initialNotes,
  isEditMode = false,
}: StuffedCookieSelectionModalProps) {
  const totalCookies = getTotalCookiesFromName(productName);
  const isSingle = totalCookies === 1;

  // Flavors from API
  const [flavors, setFlavors] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // For single: just store the selected flavor
  const [singleFlavor, setSingleFlavor] = useState('');

  // For multi: store quantity per flavor
  const [flavorQuantities, setFlavorQuantities] = useState<Record<string, number>>({});

  // Fetch flavors from API when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const fetchFlavors = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const base = API_URL ? `${API_URL}/api/product-options` : '/api/product-options';
        const response = await fetch(`${base}?category=Stuffed Cookies`);
        if (!response.ok) {
          throw new Error(`API returned ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        
        // Find the "Flavors" group - try multiple search patterns
        let flavorGroup = data.groups?.find(
          (g: { name: string }) => g.name.toLowerCase().includes('flavor')
        );
        
        // If not found, try finding any group for Stuffed Cookies category
        if (!flavorGroup && data.groups?.length > 0) {
          flavorGroup = data.groups.find(
            (g: { category_name: string }) => g.category_name === 'Stuffed Cookies'
          );
        }
        
        // If still not found, use first group (fallback)
        if (!flavorGroup && data.groups?.length > 0) {
          flavorGroup = data.groups[0];
        }
        
        // Extract available options from the group
        if (flavorGroup?.options?.length > 0) {
          // Filter to only available options
          const availableOptions = flavorGroup.options.filter(
            (opt: { is_available: boolean }) => opt.is_available !== false
          );
          
          if (availableOptions.length > 0) {
            const flavorNames = availableOptions.map((opt: { name: string }) => opt.name);
            setFlavors(flavorNames);
            const initial: Record<string, number> = {};
            flavorNames.forEach((f: string) => initial[f] = 0);
            if (initialNotes) {
              const parsed = parseNotesToPreFill(initialNotes, totalCookies === 1);
              if (totalCookies === 1) setSingleFlavor(parsed.singleFlavor);
              else for (const [name, qty] of Object.entries(parsed.flavorQuantities)) { if (name in initial) initial[name] = qty; }
            }
            setFlavorQuantities(initial);
          } else {
            setError('No flavors available. Please contact us for options.');
            setFlavors(FALLBACK_FLAVORS);
            const initial: Record<string, number> = {};
            FALLBACK_FLAVORS.forEach(f => initial[f] = 0);
            if (initialNotes) {
              const parsed = parseNotesToPreFill(initialNotes, totalCookies === 1);
              if (totalCookies === 1) setSingleFlavor(parsed.singleFlavor);
              else for (const [name, qty] of Object.entries(parsed.flavorQuantities)) { if (name in initial) initial[name] = qty; }
            }
            setFlavorQuantities(initial);
          }
        } else {
          setFlavors(FALLBACK_FLAVORS);
          const initial: Record<string, number> = {};
          FALLBACK_FLAVORS.forEach(f => initial[f] = 0);
          if (initialNotes) {
            const parsed = parseNotesToPreFill(initialNotes, totalCookies === 1);
            if (totalCookies === 1) setSingleFlavor(parsed.singleFlavor);
            else for (const [name, qty] of Object.entries(parsed.flavorQuantities)) { if (name in initial) initial[name] = qty; }
          }
          setFlavorQuantities(initial);
        }
      } catch (err) {
        console.error('Failed to fetch stuffed cookie flavors:', err);
        setError('Failed to load flavors. Using default options.');
        setFlavors(FALLBACK_FLAVORS);
        const initial: Record<string, number> = {};
        FALLBACK_FLAVORS.forEach(f => initial[f] = 0);
        if (initialNotes) {
          const parsed = parseNotesToPreFill(initialNotes, totalCookies === 1);
          if (totalCookies === 1) setSingleFlavor(parsed.singleFlavor);
          else for (const [name, qty] of Object.entries(parsed.flavorQuantities)) { if (name in initial) initial[name] = qty; }
        }
        setFlavorQuantities(initial);
      } finally {
        setLoading(false);
      }
    };

    fetchFlavors();
  }, [isOpen, initialNotes, totalCookies]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSingleFlavor('');
      setFlavorQuantities({});
      setError(null);
    }
  }, [isOpen]);

  // Calculate current total for multi-select
  const currentTotal = Object.values(flavorQuantities).reduce((sum, qty) => sum + qty, 0);
  const remaining = totalCookies - currentTotal;
  const isValid = isSingle ? singleFlavor !== '' : currentTotal === totalCookies;

  // Handle quantity change for multi-select
  const handleQuantityChange = (flavor: string, delta: number) => {
    setFlavorQuantities(prev => {
      const newQty = Math.max(0, prev[flavor] + delta);
      // Don't allow going over total
      const otherTotal = Object.entries(prev)
        .filter(([f]) => f !== flavor)
        .reduce((sum, [, qty]) => sum + qty, 0);
      if (otherTotal + newQty > totalCookies) return prev;
      return { ...prev, [flavor]: newQty };
    });
  };

  // Handle confirm button
  const handleConfirm = () => {
    if (!isValid) {
      if (isSingle) {
        alert('Please select a flavor');
      } else {
        alert(`Please select ${remaining} more cookie${remaining > 1 ? 's' : ''}`);
      }
      return;
    }

    let selection = '';
    if (isSingle) {
      selection = `Flavor: ${singleFlavor}`;
    } else {
      // Format: "2 Brownie, 3 Butterfinger, 1 Caramilk"
      const parts = flavors
        .filter(f => flavorQuantities[f] > 0)
        .map(f => `${flavorQuantities[f]} ${f}`);
      selection = parts.join(', ');
    }

    onConfirm(selection);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
            <div>
              <h2 className="text-xl font-display font-bold text-gray-900">
                Select Your Cookies
              </h2>
              <p className="text-sm text-primary-600 mt-1">
                {isEditMode ? 'Change your flavours below, then click Update.' : 'Pick your flavours below, then click Add to Cart.'}
              </p>
              <p className="text-sm text-gray-500 mt-0.5">{productName}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
              title="Close"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-600">Loading available flavors...</p>
              </div>
            ) : error ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-amber-800">{error}</p>
              </div>
            ) : flavors.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600">No flavors available. Please contact us.</p>
              </div>
            ) : isSingle ? (
              // Single cookie: dropdown
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Flavor <span className="text-red-500">*</span>
                </label>
                <select
                  value={singleFlavor}
                  onChange={(e) => setSingleFlavor(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  aria-label="Choose stuffed cookie flavor"
                  title="Choose flavor"
                >
                  <option value="">Choose a flavor...</option>
                  {flavors.map(flavor => (
                    <option key={flavor} value={flavor}>{flavor}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-2">
                  Available flavors may vary daily. What's shown is what we have today!
                </p>
              </div>
            ) : (
              // Multi-pack: quantity selectors
              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Select Flavors <span className="text-red-500">*</span>
                  </label>
                  <span className={`text-sm font-medium ${isValid ? 'text-green-600' : 'text-amber-600'}`}>
                    {isValid ? '✓ Complete' : `${remaining} more to pick`}
                  </span>
                </div>
                
                <div className="space-y-3 mb-4">
                  {flavors.map(flavor => (
                    <div key={flavor} className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <span className="text-base font-medium text-gray-800">{flavor}</span>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(flavor, -1)}
                          disabled={flavorQuantities[flavor] === 0}
                          className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed text-gray-700 font-bold text-lg transition-colors flex items-center justify-center"
                        >
                          −
                        </button>
                        <span className="w-8 text-center font-semibold text-gray-900 text-lg">
                          {flavorQuantities[flavor] || 0}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(flavor, 1)}
                          disabled={remaining === 0}
                          className="w-8 h-8 rounded-full bg-primary-100 hover:bg-primary-200 disabled:opacity-40 disabled:cursor-not-allowed text-primary-700 font-bold text-lg transition-colors flex items-center justify-center"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Summary */}
                {currentTotal > 0 && (
                  <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 mb-4">
                    <p className="text-sm font-medium text-primary-800 mb-1">Your Selection:</p>
                    <p className="text-sm text-primary-700">
                      {flavors.filter(f => flavorQuantities[f] > 0)
                        .map(f => `${flavorQuantities[f]} ${f}`)
                        .join(' • ')}
                    </p>
                  </div>
                )}

                <p className="text-xs text-gray-500">
                  Available flavors may vary daily. What's shown is what we have today!
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm text-gray-600">Total: <span className="font-semibold text-gray-900">${productPrice.toFixed(2)}</span></p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={!isValid}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {isEditMode ? 'Update flavours' : 'Add to Cart'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

